import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import { 
  GraduationCap, 
  ArrowLeft,
  CreditCard,
  AlertTriangle,
  Clock
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const RefundPolicyPage = () => {
  return (
    <div className="min-h-screen bg-stone-50" data-testid="refund-policy-page">
      {/* Header */}
      <header className="bg-white border-b border-stone-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <Link to="/" className="flex items-center gap-2">
                <GraduationCap className="h-8 w-8 text-indigo-700" />
                <span className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">
                  Gifted Academy Tutors
                </span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-12 md:py-16 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center">
              <CreditCard className="h-8 w-8 text-amber-700" />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-medium text-slate-900">
                Payment & Refund Policy
              </h1>
              <p className="text-slate-500">Last updated: March 2026</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="prose prose-slate max-w-none">
            <div className="bg-white rounded-2xl border border-stone-200 p-8 md:p-12 space-y-8">
              
              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  1. Payment Methods
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Gifted Academy Tutors accepts payments through Stripe, a secure payment processing platform. We accept major credit cards and debit cards. All transactions are processed securely and your payment information is never stored on our servers.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  2. Tutoring Packages
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  We offer tutoring packages based on the number of hours purchased:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <h4 className="font-semibold text-slate-900 mb-2">High School</h4>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>Standard: $45/hour</li>
                      <li>10-hour package: $40/hour</li>
                      <li>15+ hour package: $35/hour</li>
                    </ul>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <h4 className="font-semibold text-slate-900 mb-2">University</h4>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>Standard: $50/hour</li>
                      <li>10-hour package: $45/hour</li>
                      <li>15+ hour package: $40/hour</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  3. Package Expiration Policy
                </h2>
                <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-amber-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-slate-700 leading-relaxed">
                        <strong>Tutoring packages are valid for 6 months from the date of purchase.</strong>
                      </p>
                      <p className="text-slate-600 leading-relaxed mt-2">
                        Unused tutoring hours remaining after 6 months will automatically expire and cannot be redeemed.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  4. Refund Eligibility
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Refunds may be requested only for <strong>unused tutoring hours</strong>. Used hours, including sessions marked as no-shows, are not eligible for refund.
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Refund requests must be submitted in writing via email</li>
                  <li>Refunds are processed within 10-14 business days</li>
                  <li>Expired package hours are not eligible for refund</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  5. Refund Calculation
                </h2>
                <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-slate-700 leading-relaxed">
                        If a refund is approved, the refund amount will be calculated based on the <strong>unused tutoring hours only</strong>.
                      </p>
                      <p className="text-slate-600 leading-relaxed mt-2">
                        Gifted Academy Tutors may deduct <strong>administrative and processing fees of up to 40%</strong> of the remaining unused package value.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  6. Refund Method
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Refunds will be issued using the <strong>original payment method</strong> whenever possible. If the original payment method is no longer available, we will work with you to find an alternative refund method.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  7. Non-Refundable Items
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  The following are not eligible for refunds:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Completed tutoring sessions</li>
                  <li>Sessions marked as no-show</li>
                  <li>Sessions cancelled less than 6 hours before the scheduled time</li>
                  <li>Expired package hours</li>
                  <li>Administrative fees from previous refunds</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  8. How to Request a Refund
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  To request a refund, please send an email to <strong>contact@giftedacademytutors.ca</strong> with the following information:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                  <li>Your full name and account email</li>
                  <li>Date of original purchase</li>
                  <li>Number of unused hours</li>
                  <li>Reason for refund request</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  9. Disputes
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  If you have a dispute regarding charges or refunds, please contact us before initiating a chargeback with your bank. We are committed to resolving payment issues fairly and promptly.
                </p>
              </div>

            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RefundPolicyPage;
